import {
  ActionRes,
  ReportKey,
  ReportsDataMap,
  ReportsFilterOptionsResponse,
  ReportsFilters,
} from '@models/interfaces';
import {
  ReportsActions,
  ReportsErrorCode,
  ReportsRequestActionPayload,
} from '../actions/reports.actions';

type ActionWithType = ((payload?: unknown) => ActionRes) & { type: string };

export interface ReportsState {
  data: Partial<ReportsDataMap>;
  errors: Partial<Record<ReportKey, boolean | ReportsErrorCode>>;
  filterOptions: ReportsFilterOptionsResponse | null;
  filterOptionsError: boolean;
  filterOptionsLoading: boolean;
  filterOptionsRequestId?: string;
  filters: ReportsFilters;
  loadedReports: Partial<Record<ReportKey, string>>;
  loadingReportHashes: Partial<Record<ReportKey, string>>;
  loadingReportIds: Partial<Record<ReportKey, string>>;
  loadingReports: Partial<Record<ReportKey, boolean>>;
  selectedReport: ReportKey;
}

const clearedReports = {
  data: {},
  errors: {},
  loadedReports: {},
  loadingReportHashes: {},
  loadingReportIds: {},
  loadingReports: {},
};

const initialState: ReportsState = {
  ...clearedReports,
  filterOptions: null,
  filterOptionsError: false,
  filterOptionsLoading: false,
  filters: { page: 1, size: 25 },
  selectedReport: ReportKey.OverdueTasks,
};

const getActionType = (action: unknown): string =>
  (action as ActionWithType).type;

const isRequest = (payload: unknown): payload is ReportsRequestActionPayload =>
  !!payload &&
  typeof payload === 'object' &&
  typeof (payload as ReportsRequestActionPayload).requestId === 'string' &&
  typeof (payload as ReportsRequestActionPayload).requestHash === 'string';

export const getReportsFilterHash = (filters: ReportsFilters = {}): string =>
  JSON.stringify(
    Object.keys(filters)
      .sort()
      .reduce((result: Record<string, unknown>, key: string) => {
        const value = filters[key as keyof ReportsFilters];

        if (
          value !== undefined &&
          value !== null &&
          value !== '' &&
          !(Array.isArray(value) && !value.length)
        ) {
          return {
            ...result,
            [key]: Array.isArray(value) ? [...value].sort() : value,
          };
        }

        return result;
      }, {}),
  );

const reportActions = {
  [ReportKey.PermittingOverview]: ReportsActions.getPermittingOverview,
  [ReportKey.BPExpiration]: ReportsActions.getBPExpiration,
  [ReportKey.OverdueTasks]: ReportsActions.getOverdueTasks,
  [ReportKey.DatesAtAGlance]: ReportsActions.getDatesAtAGlance,
  [ReportKey.StructuralPassFail]: ReportsActions.getStructuralPassFail,
  [ReportKey.ProjectDuration]: ReportsActions.getProjectDuration,
  [ReportKey.NewClosedProjects]: ReportsActions.getNewClosedProjects,
};

export const reducer = (
  incomingState: ReportsState | undefined,
  { payload, type }: ActionRes,
): ReportsState => {
  const state = incomingState ?? initialState;

  if (type === getActionType(ReportsActions.invalidateReportsData)) {
    return {
      ...state,
      ...clearedReports,
      filterOptionsRequestId: undefined,
      filterOptionsLoading: false,
    };
  }

  if (type === getActionType(ReportsActions.setFilters)) {
    return {
      ...state,
      ...clearedReports,
      data: state.data,
      filters: payload as ReportsFilters,
    };
  }

  if (type === getActionType(ReportsActions.setSelectedReport)) {
    return {
      ...state,
      ...clearedReports,
      selectedReport: payload as ReportKey,
    };
  }

  if (!isRequest(payload)) {
    return state;
  }

  if (type === getActionType(ReportsActions.getFilterOptions.start)) {
    return {
      ...state,
      filterOptionsError: false,
      filterOptionsLoading: true,
      filterOptionsRequestId: payload.requestId,
    };
  }

  if (
    type === getActionType(ReportsActions.getFilterOptions.done) ||
    type === getActionType(ReportsActions.getFilterOptions.fail)
  ) {
    if (payload.requestId !== state.filterOptionsRequestId) {
      return state;
    }

    const success =
      type === getActionType(ReportsActions.getFilterOptions.done) &&
      'data' in payload;

    return {
      ...state,
      filterOptions: success
        ? (
            payload as ReportsRequestActionPayload & {
              data: ReportsFilterOptionsResponse;
            }
          ).data
        : state.filterOptions,
      filterOptionsLoading: false,
      filterOptionsError: !success,
      filterOptionsRequestId: undefined,
    };
  }

  const reportKey = Object.values(ReportKey).find(key =>
    Object.values(reportActions[key]).some(
      action => getActionType(action) === type,
    ),
  );

  if (!reportKey) {
    return state;
  }

  const actions = reportActions[reportKey];

  if (type === getActionType(actions.start)) {
    return {
      ...state,
      errors: { ...state.errors, [reportKey]: false },
      loadingReports: { ...state.loadingReports, [reportKey]: true },
      loadingReportHashes: {
        ...state.loadingReportHashes,
        [reportKey]: payload.requestHash,
      },
      loadingReportIds: {
        ...state.loadingReportIds,
        [reportKey]: payload.requestId,
      },
    };
  }

  if (
    state.loadingReportIds[reportKey] !== payload.requestId ||
    getReportsFilterHash(state.filters) !== payload.requestHash
  ) {
    return state;
  }

  const success = type === getActionType(actions.done) && 'data' in payload;

  return {
    ...state,
    data: success
      ? {
          ...state.data,
          [reportKey]: (
            payload as ReportsRequestActionPayload & { data: unknown }
          ).data,
        }
      : state.data,
    errors: {
      ...state.errors,
      [reportKey]: success ? false : payload.errorCode ?? true,
    },
    loadedReports: {
      ...state.loadedReports,
      [reportKey]: success ? payload.requestHash : undefined,
    },
    loadingReports: { ...state.loadingReports, [reportKey]: false },
    loadingReportHashes: {
      ...state.loadingReportHashes,
      [reportKey]: undefined,
    },
    loadingReportIds: { ...state.loadingReportIds, [reportKey]: undefined },
  };
};
